import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration <= 0) return "Not recorded";

  const totalSeconds = Math.ceil(duration);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds || !parts.length) parts.push(`${seconds}s`);
  return parts.join(" ");
};

const buildPagerLabel = (pagination) => {
  if (!pagination?.totalDocs) {
    return "0 results";
  }

  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.totalDocs);
  return `${start}-${end} of ${pagination.totalDocs}`;
};

const answerLabel = (key, text) => {
  const normalizedKey = String(key || "").replace("option", "Option ");
  const normalizedText = String(text || "").trim();
  if (!normalizedKey && !normalizedText) return "-";
  return [normalizedKey, normalizedText].filter(Boolean).join(": ");
};

const ReviewQuestionCard = ({ item }) => {
  const status = item?.is_skipped ? "Skipped" : item?.is_correct ? "Correct" : "Incorrect";
  const statusColor = item?.is_skipped ? "warning" : item?.is_correct ? "success" : "error";
  const visibleOptions = (item?.options || []).filter((option) => option?.text || option?.text_hindi);

  return (
    <MDBox
      p={{ xs: 1.75, md: 2.25 }}
      borderRadius="xl"
      sx={{
        border: "1px solid rgba(126, 170, 220, 0.16)",
        background: "linear-gradient(180deg, rgba(17,35,56,0.86), rgba(10,23,39,0.9))",
      }}
    >
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1}
      >
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <MDTypography variant="h6" sx={{ color: "#edf4ff" }}>
            Question {item?.order || "-"}
          </MDTypography>
          <Chip label={status} color={statusColor} size="small" />
          {Number(item?.time_spent_seconds || 0) > 0 ? (
            <Chip
              label={`${Number(item.time_spent_seconds)} sec`}
              size="small"
              sx={{ color: "#c7d8eb", backgroundColor: "rgba(126,170,220,0.12)" }}
            />
          ) : null}
        </MDBox>
        {item?.can_edit && item?.question_id ? (
          <MDButton
            component={Link}
            to={`/updateQuestion/${item.question_id}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            color="info"
            size="small"
          >
            Edit Question
          </MDButton>
        ) : (
          <MDTypography variant="caption" sx={{ color: "#7288a3" }}>
            Original question is unavailable for editing
          </MDTypography>
        )}
      </MDBox>

      <MDTypography
        variant="button"
        display="block"
        mt={1.75}
        sx={{ color: "#ffffff", fontSize: "0.95rem", lineHeight: 1.65, whiteSpace: "pre-wrap" }}
      >
        {item?.question || "Question text is unavailable for this older attempt."}
      </MDTypography>
      {item?.question_hindi ? (
        <MDTypography
          variant="button"
          display="block"
          mt={0.75}
          sx={{ color: "#b8cbe0", lineHeight: 1.65, whiteSpace: "pre-wrap" }}
        >
          {item.question_hindi}
        </MDTypography>
      ) : null}

      {visibleOptions.length ? (
        <Grid container spacing={1.25} mt={0.5}>
          {visibleOptions.map((option) => {
            const isCorrect = Boolean(option?.is_correct_answer);
            const isSelected = Boolean(option?.is_user_answer);
            const borderColor = isCorrect
              ? "rgba(69, 196, 106, 0.7)"
              : isSelected
              ? "rgba(255, 105, 120, 0.68)"
              : "rgba(126, 170, 220, 0.14)";

            return (
              <Grid item xs={12} md={6} key={option.key}>
                <MDBox
                  p={1.4}
                  borderRadius="lg"
                  sx={{
                    height: "100%",
                    border: `1px solid ${borderColor}`,
                    backgroundColor: isCorrect
                      ? "rgba(69, 196, 106, 0.1)"
                      : isSelected
                      ? "rgba(255, 105, 120, 0.08)"
                      : "rgba(255,255,255,0.025)",
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" gap={1}>
                    <MDTypography variant="button" sx={{ color: "#edf4ff", lineHeight: 1.5 }}>
                      {answerLabel(option.key, option.text)}
                    </MDTypography>
                    <MDBox display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                      {isSelected ? <Chip label="User" size="small" color="info" /> : null}
                      {isCorrect ? <Chip label="Correct" size="small" color="success" /> : null}
                    </MDBox>
                  </MDBox>
                  {option?.text_hindi && option.text_hindi !== option.text ? (
                    <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: "#9db4cf" }}>
                      {option.text_hindi}
                    </MDTypography>
                  ) : null}
                </MDBox>
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      <Grid container spacing={1.25} mt={0.75}>
        <Grid item xs={12} md={6}>
          <MDBox p={1.4} borderRadius="lg" sx={{ backgroundColor: "rgba(88,166,255,0.09)" }}>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#73b6ff" }}>
              USER&apos;S ATTEMPTED ANSWER
            </MDTypography>
            <MDTypography variant="button" display="block" mt={0.4} sx={{ color: "#edf4ff" }}>
              {item?.is_skipped
                ? "Skipped"
                : answerLabel(item?.user_answer, item?.user_answer_text)}
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox p={1.4} borderRadius="lg" sx={{ backgroundColor: "rgba(69,196,106,0.09)" }}>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#66d48a" }}>
              CORRECT ANSWER
            </MDTypography>
            <MDTypography variant="button" display="block" mt={0.4} sx={{ color: "#edf4ff" }}>
              {answerLabel(item?.correct_answer, item?.correct_answer_text)}
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={1.5} p={1.5} borderRadius="lg" sx={{ backgroundColor: "rgba(255,255,255,0.035)" }}>
        <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#9db4cf" }}>
          DESCRIPTION / EXPLANATION
        </MDTypography>
        <MDTypography
          variant="button"
          display="block"
          mt={0.5}
          sx={{ color: "#dbe8f6", lineHeight: 1.65, whiteSpace: "pre-wrap" }}
        >
          {item?.description || "No description is available for this question."}
        </MDTypography>
        {item?.description_hindi && item.description_hindi !== item.description ? (
          <MDTypography
            variant="button"
            display="block"
            mt={0.75}
            sx={{ color: "#aebfd2", lineHeight: 1.65, whiteSpace: "pre-wrap" }}
          >
            {item.description_hindi}
          </MDTypography>
        ) : null}
      </MDBox>
    </MDBox>
  );
};

ReviewQuestionCard.propTypes = {
  item: PropTypes.shape({
    order: PropTypes.number,
    question_id: PropTypes.string,
    can_edit: PropTypes.bool,
    question: PropTypes.string,
    question_hindi: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    user_answer: PropTypes.string,
    user_answer_text: PropTypes.string,
    correct_answer: PropTypes.string,
    correct_answer_text: PropTypes.string,
    is_correct: PropTypes.bool,
    is_skipped: PropTypes.bool,
    description: PropTypes.string,
    description_hindi: PropTypes.string,
    time_spent_seconds: PropTypes.number,
  }).isRequired,
};

const SummaryCard = ({ label, value, accent }) => (
  <MDBox
    p={2.25}
    borderRadius="xl"
    sx={{
      background:
        "linear-gradient(180deg, rgba(13,25,40,0.96) 0%, rgba(10,20,33,0.94) 100%)",
      border: "1px solid rgba(126, 170, 220, 0.12)",
      minHeight: 108,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 18px 40px rgba(0,0,0,0.2)",
    }}
  >
    <MDBox
      sx={{
        position: "absolute",
        inset: "0 auto 0 0",
        width: 5,
        background: accent,
      }}
    />
    <MDTypography
      variant="caption"
      textTransform="uppercase"
      fontWeight="bold"
      sx={{ color: "#8fa7c4", letterSpacing: "0.08em" }}
    >
      {label}
    </MDTypography>
    <MDTypography variant="h4" mt={1.2} fontWeight="bold" sx={{ color: "#edf4ff" }}>
      {value}
    </MDTypography>
  </MDBox>
);

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  accent: PropTypes.string.isRequired,
};

const SectionList = ({
  title,
  rows,
  pagination,
  onPageChange,
  onEntriesPerPageChange,
  emptyText,
  fields,
}) => (
  <Card
    sx={{
      background: "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
      border: "1px solid rgba(126, 170, 220, 0.12)",
      boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
    }}
  >
    <MDBox p={2.5}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={1}
      >
        <MDTypography variant="h6" sx={{ color: "#edf4ff" }}>
          {title}
        </MDTypography>
        <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
          {buildPagerLabel(pagination)}
        </MDTypography>
      </MDBox>

      <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
        {rows?.length ? (
          rows.map((row, index) => (
            <MDBox
              key={row._id || `${title}-${index}`}
              p={1.5}
              borderRadius="xl"
              sx={{
                border: "1px solid rgba(126, 170, 220, 0.12)",
                background:
                  "linear-gradient(180deg, rgba(17,35,56,0.8) 0%, rgba(13,29,47,0.78) 100%)",
              }}
            >
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                flexDirection={{ xs: "column", md: "row" }}
                gap={1}
                mb={1}
              >
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#edf4ff" }}>
                  #{(pagination?.page - 1 || 0) * (pagination?.limit || 10) + index + 1}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
                  {formatDate(row.createdAt)}
                </MDTypography>
              </MDBox>

              <Grid container spacing={1.25}>
                {fields.map((field) => (
                  <Grid item xs={12} sm={6} md={4} key={field.key}>
                    <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                      {field.label}
                    </MDTypography>
                    <MDTypography variant="button" display="block" sx={{ color: "#edf4ff" }}>
                      {field.render ? field.render(row) : row[field.key] ?? "-"}
                    </MDTypography>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          ))
        ) : (
          <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
            {emptyText}
          </MDTypography>
        )}
      </MDBox>

      <MDBox
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <MDBox display="flex" alignItems="center" gap={1.25}>
          <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
            Rows per page
          </MDTypography>
          <TextField
            select
            size="small"
            value={pagination?.limit || 10}
            onChange={(event) => onEntriesPerPageChange(Number(event.target.value))}
            sx={{ width: 82 }}
          >
            {[10, 25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>{size}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        {pagination?.totalPages > 1 ? (
          <Pagination
            page={pagination.page}
            count={pagination.totalPages}
            color="primary"
            variant="outlined"
            shape="rounded"
            sx={agriPaginationSx}
            onChange={(event, page) => onPageChange(page)}
          />
        ) : null}
      </MDBox>
    </MDBox>
  </Card>
);

SectionList.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.shape({
    page: PropTypes.number,
    limit: PropTypes.number,
    totalDocs: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  onEntriesPerPageChange: PropTypes.func,
  emptyText: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
    })
  ).isRequired,
};

SectionList.defaultProps = {
  rows: [],
  pagination: null,
  onPageChange: () => {},
  onEntriesPerPageChange: () => {},
};

export default function UserPracticeTests() {
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [practicePage, setPracticePage] = useState(1);
  const [mockPage, setMockPage] = useState(1);
  const [testSeriesPage, setTestSeriesPage] = useState(1);
  const [liveTestPage, setLiveTestPage] = useState(1);
  const [friendsTestPage, setFriendsTestPage] = useState(1);
  const [limits, setLimits] = useState({
    practice: 10,
    mock: 10,
    series: 10,
    live: 10,
    friends: 10,
  });
  const [activeTab, setActiveTab] = useState("practice");
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("warning");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState("");

  useEffect(() => {
    const loadAttemptHistory = async () => {
      setLoading(true);
      if (init() !== "success") {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${Constants.getUrls.getUserDetails}${id}`, {
          params: {
            practice_page: practicePage,
            practice_limit: limits.practice,
            mock_page: mockPage,
            mock_limit: limits.mock,
            test_series_page: testSeriesPage,
            test_series_limit: limits.series,
            live_test_page: liveTestPage,
            live_test_limit: limits.live,
            friends_test_page: friendsTestPage,
            friends_test_limit: limits.friends,
            transaction_page: 1,
            transaction_limit: 1,
          },
        });
        setDetails(response?.data?.data || null);
      } catch (error) {
        setNotiColor("warning");
        setMessage(error?.response?.data?.message || "Unable to load user test history.");
        setNotification(true);
      } finally {
        setLoading(false);
      }
    };

    loadAttemptHistory();
  }, [id, practicePage, mockPage, testSeriesPage, liveTestPage, friendsTestPage, limits]);

  const openPracticeAttempt = useCallback(
    async (attemptId) => {
      setReviewOpen(true);
      setReviewLoading(true);
      setReview(null);
      setReviewError("");

      if (init() !== "success") {
        setReviewLoading(false);
        setReviewError("Your admin session has expired. Please sign in again.");
        return;
      }

      try {
        const response = await axios.get(
          `${Constants.getUrls.getUserPracticeAttempt}${id}/practice-attempts/${attemptId}`
        );
        setReview(response?.data?.data || null);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Unable to load this practice attempt.";
        setReviewError(errorMessage);
        setNotiColor("error");
        setMessage(errorMessage);
        setNotification(true);
      } finally {
        setReviewLoading(false);
      }
    },
    [id]
  );

  const tabConfig = useMemo(
    () => [
      {
        key: "practice",
        label: "Practice Test",
        count: details?.overview?.practice_tests_given || 0,
        title: "Practice Test History",
        rows: details?.practice_tests || [],
        pagination: details?.practice_tests_pagination,
        onPageChange: setPracticePage,
        onEntriesPerPageChange: (limit) => {
          setLimits((prev) => ({ ...prev, practice: limit }));
          setPracticePage(1);
        },
        emptyText: "No practice tests found yet.",
        fields: [
          { key: "subject_name", label: "Subject" },
          { key: "topic_name", label: "Topic" },
          { key: "level_name", label: "Level" },
          { key: "points_earned", label: "Points" },
          { key: "result", label: "Score" },
          {
            key: "total_time_spent_seconds",
            label: "Time Taken",
            render: (row) => formatDuration(row.total_time_spent_seconds),
          },
          {
            key: "accuracyPercent",
            label: "Accuracy",
            render: (row) => `${Number(row.accuracyPercent || 0)}%`,
          },
          { key: "rightAttempt", label: "Right" },
          { key: "wrongAttempt", label: "Wrong" },
          { key: "skipAttempt", label: "Skip" },
          { key: "performance", label: "Performance" },
          {
            key: "review",
            label: "Answer Review",
            render: (row) => (
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => openPracticeAttempt(row._id)}
              >
                View Attempt
              </MDButton>
            ),
          },
        ],
      },
      {
        key: "mock",
        label: "Mock Test",
        count: details?.overview?.mock_tests_given || 0,
        title: "Mock Test History",
        rows: details?.mock_tests || [],
        pagination: details?.mock_tests_pagination,
        onPageChange: setMockPage,
        onEntriesPerPageChange: (limit) => {
          setLimits((prev) => ({ ...prev, mock: limit }));
          setMockPage(1);
        },
        emptyText: "No mock test history found yet.",
        fields: [
          { key: "test_series_name", label: "Mock Test" },
          { key: "test_status", label: "Status" },
          { key: "test_submitted", label: "Submitted" },
          { key: "points_earned", label: "Points" },
          { key: "result", label: "Score" },
          {
            key: "total_time_spent_seconds",
            label: "Time Taken",
            render: (row) => formatDuration(row.total_time_spent_seconds),
          },
          {
            key: "accuracyPercent",
            label: "Accuracy",
            render: (row) => `${Number(row.accuracyPercent || 0)}%`,
          },
          { key: "rank", label: "Rank", render: (row) => row.rank || "-" },
          { key: "totalQuestions", label: "Questions Solved" },
          { key: "payment_type", label: "Access Type" },
        ],
      },
      {
        key: "series",
        label: "Test Series",
        count: details?.overview?.scheduled_tests_joined || 0,
        title: "Test Series History",
        rows: details?.test_series_attempts || [],
        pagination: details?.test_series_attempts_pagination,
        onPageChange: setTestSeriesPage,
        onEntriesPerPageChange: (limit) => {
          setLimits((prev) => ({ ...prev, series: limit }));
          setTestSeriesPage(1);
        },
        emptyText: "No test series history found yet.",
        fields: [
          { key: "test_series_name", label: "Test Series" },
          { key: "test_status", label: "Status" },
          { key: "test_submitted", label: "Submitted" },
          { key: "points_earned", label: "Points" },
          { key: "result", label: "Score" },
          {
            key: "total_time_spent_seconds",
            label: "Time Taken",
            render: (row) => formatDuration(row.total_time_spent_seconds),
          },
          {
            key: "accuracyPercent",
            label: "Accuracy",
            render: (row) => `${Number(row.accuracyPercent || 0)}%`,
          },
          { key: "rank", label: "Rank", render: (row) => row.rank || "-" },
          { key: "totalQuestions", label: "Questions Solved" },
          { key: "payment_type", label: "Access Type" },
        ],
      },
      {
        key: "live",
        label: "Live Test",
        count: details?.overview?.live_tests_joined || 0,
        title: "Live Test History",
        rows: details?.live_tests || [],
        pagination: details?.live_tests_pagination,
        onPageChange: setLiveTestPage,
        onEntriesPerPageChange: (limit) => {
          setLimits((prev) => ({ ...prev, live: limit }));
          setLiveTestPage(1);
        },
        emptyText: "No live test history found yet.",
        fields: [
          { key: "live_test_name", label: "Live Test" },
          { key: "participant_status", label: "Participant Status" },
          { key: "test_submitted", label: "Submitted" },
          { key: "points_earned", label: "Points" },
          { key: "result", label: "Score" },
          {
            key: "total_time_spent_seconds",
            label: "Time Taken",
            render: (row) => formatDuration(row.total_time_spent_seconds),
          },
          {
            key: "accuracyPercent",
            label: "Accuracy",
            render: (row) => `${Number(row.accuracyPercent || 0)}%`,
          },
          { key: "rank", label: "Rank", render: (row) => row.rank || "-" },
          { key: "totalQuestions", label: "Questions Solved" },
          { key: "live_test_status", label: "Test Status" },
        ],
      },
      {
        key: "friends",
        label: "Friend Test",
        count: details?.overview?.friends_tests_joined || 0,
        title: "Friend Test History",
        rows: details?.friends_tests || [],
        pagination: details?.friends_tests_pagination,
        onPageChange: setFriendsTestPage,
        onEntriesPerPageChange: (limit) => {
          setLimits((prev) => ({ ...prev, friends: limit }));
          setFriendsTestPage(1);
        },
        emptyText: "No friend test history found yet.",
        fields: [
          { key: "friends_test_title", label: "Friend Test" },
          { key: "attempt_status", label: "Attempt Status" },
          { key: "test_status", label: "Series Status" },
          { key: "points_earned", label: "Points" },
          { key: "result", label: "Score" },
          {
            key: "total_time_spent_seconds",
            label: "Time Taken",
            render: (row) => formatDuration(row.total_time_spent_seconds),
          },
          {
            key: "accuracyPercent",
            label: "Accuracy",
            render: (row) => `${Number(row.accuracyPercent || 0)}%`,
          },
          { key: "rightAttempt", label: "Right" },
          { key: "wrongAttempt", label: "Wrong" },
          { key: "totalQuestions", label: "Questions Solved" },
        ],
      },
    ],
    [details, openPracticeAttempt]
  );

  const activeSection = tabConfig.find((item) => item.key === activeTab) || tabConfig[0];

  const summaryCards = useMemo(
    () => [
      {
        label: "Practice Tests",
        value: details?.overview?.practice_tests_given || 0,
        accent: "linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)",
      },
      {
        label: "Mock Tests",
        value: details?.overview?.mock_tests_given || 0,
        accent: "linear-gradient(180deg, #EC4899 0%, #DB2777 100%)",
      },
      {
        label: "Test Series",
        value: details?.overview?.scheduled_tests_joined || 0,
        accent: "linear-gradient(180deg, #0F766E 0%, #0F766E 100%)",
      },
      {
        label: "Live Tests",
        value: details?.overview?.live_tests_joined || 0,
        accent: "linear-gradient(180deg, #D97706 0%, #F59E0B 100%)",
      },
      {
        label: "Friend Tests",
        value: details?.overview?.friends_tests_joined || 0,
        accent: "linear-gradient(180deg, #7C3AED 0%, #6D28D9 100%)",
      },
      {
        label: "Total MCQ Solved",
        value: details?.overview?.total_mcq_solved || 0,
        accent: "linear-gradient(180deg, #475569 0%, #334155 100%)",
      },
      {
        label: "Earned Points",
        value: details?.overview?.earned_points || details?.user?.earned_points || 0,
        accent: "linear-gradient(180deg, #059669 0%, #047857 100%)",
      },
    ],
    [details]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card
          sx={{
            background:
              "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
            border: "1px solid rgba(126, 170, 220, 0.12)",
          }}
        >
          <MDBox
            p={3}
            sx={{
              background:
                "linear-gradient(135deg, rgba(15,23,42,1) 0%, rgba(30,41,59,0.96) 50%, rgba(51,65,85,0.94) 100%)",
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <MDBox>
                <MDTypography variant="h4" color="white">
                  {details?.user?.name || "User"} Test History
                </MDTypography>
                <MDTypography variant="button" color="white" opacity={0.8}>
                  Dedicated performance view across practice, series, live, and friend tests
                </MDTypography>
              </MDBox>
              <MDButton component={Link} to={`/user-details/${id}`} variant="gradient" color="info">
                Back to User Details
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        <Grid container spacing={2} mt={0.5}>
          {summaryCards.map((item) => (
            <Grid item xs={12} sm={6} xl={2} key={item.label}>
              <SummaryCard label={item.label} value={item.value} accent={item.accent} />
            </Grid>
          ))}
        </Grid>

        <Card
          sx={{
            mt: 3,
            background:
              "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
            border: "1px solid rgba(126, 170, 220, 0.12)",
          }}
        >
          <MDBox p={1.5}>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabConfig.map((item) => (
                <Tab
                  key={item.key}
                  value={item.key}
                  label={`${item.label} (${item.count})`}
                  sx={{
                    fontWeight: 700,
                    textTransform: "none",
                    color: "#8fa7c4",
                    "&.Mui-selected": {
                      color: "#edf4ff",
                    },
                  }}
                />
              ))}
            </Tabs>
          </MDBox>
        </Card>

        <MDBox mt={3}>
          {loading ? (
            <Card
              sx={{
                background:
                  "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
                border: "1px solid rgba(126, 170, 220, 0.12)",
              }}
            >
              <MDBox p={3}>
                <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                  Loading test history...
                </MDTypography>
              </MDBox>
            </Card>
          ) : (
            <SectionList
              title={activeSection.title}
              rows={activeSection.rows}
              pagination={activeSection.pagination}
              onPageChange={activeSection.onPageChange}
              onEntriesPerPageChange={activeSection.onEntriesPerPageChange}
              emptyText={activeSection.emptyText}
              fields={activeSection.fields}
            />
          )}
        </MDBox>
      </MDBox>

      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            color: "#edf4ff",
            borderRadius: "18px",
            border: "1px solid rgba(126,170,220,0.18)",
            background: "linear-gradient(180deg, #0d1d2f 0%, #07121f 100%)",
            boxShadow: "0 30px 90px rgba(0,0,0,0.6)",
          },
        }}
      >
        <DialogContent sx={{ p: 0, background: "transparent" }}>
          <MDBox
            px={{ xs: 2, md: 3 }}
            py={2.25}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
            sx={{
              borderBottom: "1px solid rgba(126,170,220,0.14)",
              background:
                "radial-gradient(circle at top right, rgba(47,155,255,0.18), transparent 38%)",
            }}
          >
            <MDBox>
              <MDTypography variant="h5" sx={{ color: "#ffffff" }}>
                Practice Attempt Review
              </MDTypography>
              <MDTypography variant="button" display="block" mt={0.5} sx={{ color: "#9db4cf" }}>
                {review
                  ? [
                      review?.subject?.subject_name,
                      review?.topic?.topic_name,
                      review?.level?.level_name,
                      formatDate(review?.createdAt),
                    ]
                      .filter(Boolean)
                      .join(" • ")
                  : "Loading the user’s submitted answers..."}
              </MDTypography>
            </MDBox>
            <IconButton
              aria-label="Close attempt review"
              onClick={() => setReviewOpen(false)}
              sx={{ color: "#c7d8eb" }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>

          <MDBox p={{ xs: 2, md: 3 }}>
            {reviewLoading ? (
              <MDBox py={6} textAlign="center">
                <MDTypography variant="button" sx={{ color: "#9db4cf" }}>
                  Loading submitted answers...
                </MDTypography>
              </MDBox>
            ) : reviewError ? (
              <MDBox
                p={2}
                borderRadius="lg"
                sx={{
                  color: "#ffc3ca",
                  border: "1px solid rgba(255,105,120,0.3)",
                  backgroundColor: "rgba(255,105,120,0.08)",
                }}
              >
                <MDTypography variant="button" sx={{ color: "inherit" }}>
                  {reviewError}
                </MDTypography>
              </MDBox>
            ) : review ? (
              <>
                <Grid container spacing={1.25} mb={2.5}>
                  {[
                    { label: "Score", value: review.result || `${review.rightAttempt}/${review.totalQuestions}` },
                    { label: "Time Taken", value: formatDuration(review.total_time_spent_seconds) },
                    { label: "Correct", value: review.rightAttempt || 0 },
                    { label: "Incorrect", value: review.wrongAttempt || 0 },
                    { label: "Skipped", value: review.skipAttempt || 0 },
                    { label: "Accuracy", value: `${Number(review.accuracyPercent || 0)}%` },
                  ].map((item) => (
                    <Grid item xs={6} sm={4} md={2} key={item.label}>
                      <MDBox
                        p={1.5}
                        borderRadius="lg"
                        sx={{
                          border: "1px solid rgba(126,170,220,0.12)",
                          backgroundColor: "rgba(255,255,255,0.035)",
                        }}
                      >
                        <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
                          {item.label}
                        </MDTypography>
                        <MDTypography variant="h6" mt={0.35} sx={{ color: "#ffffff" }}>
                          {item.value}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>

                <MDTypography variant="caption" display="block" mb={2.5} sx={{ color: "#8fa7c4" }}>
                  Practice timing comes from the app and is an indicator, not proof of cheating.
                </MDTypography>

                <MDBox display="flex" flexDirection="column" gap={1.75}>
                  {(review.review_questions || []).length ? (
                    review.review_questions.map((question) => (
                      <ReviewQuestionCard
                        key={`${review._id}-${question.order}-${question.question_id || "missing"}`}
                        item={question}
                      />
                    ))
                  ) : (
                    <MDTypography variant="button" sx={{ color: "#9db4cf" }}>
                      No submitted answers are stored for this attempt.
                    </MDTypography>
                  )}
                </MDBox>
              </>
            ) : null}
          </MDBox>
        </DialogContent>
      </Dialog>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz Admin"
        content={message}
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}
